import { saveHotspots } from './api-service.js';
import { createImageModal } from './image-handler.js';

/**
 * R2 Worker URL for assets
 */
const ASSETS_WORKER_URL = 'https://model-viewer-assets.adrian-a87.workers.dev';

/**
 * Initialize hotspot manager
 * @param {HTMLElement} modelViewer - Model viewer element
 * @param {HTMLElement} hotspotPreview - Hotspot preview element
 * @param {HTMLElement} hotspotEditPanel - Hotspot edit panel element
 * @param {Object} imageHandler - Image handler object
 * @returns {Object} - Object with hotspot management methods
 */
export function initHotspotManager(modelViewer, hotspotPreview, hotspotEditPanel, imageHandler) {
    let isPlacing = false;
    let mouseX = 0;
    let mouseY = 0;
    let currentHotspot = null;
    let selectedColor = 'blue';
    let hotspotCounter = 0;
    
    /**
     * Helper function to extract model ID from URL
     * Works with both API paths and direct R2 URLs
     * @param {string} url - The URL of the model
     * @returns {string} - The extracted model ID
     */
    function extractModelIdFromUrl(url) {
        if (!url) return 'unknown-model';
        
        // Get the filename from the URL
        const urlParts = url.split('/');
        const filename = urlParts[urlParts.length - 1];
        
        // Extract the model ID (without extension)
        return filename.split('.')[0];
    }

    const colorOptions = document.querySelectorAll('.color-option');
    const nameInput = document.getElementById('hotspot-name');
    const textInput = document.getElementById('hotspot-text');
    const previewImage = document.getElementById('image-preview');
    
    colorOptions.forEach(option => {
        option.addEventListener('click', function() {
            selectedColor = this.dataset.color;
            colorOptions.forEach(opt => opt.style.outline = 'none');
            this.style.outline = '2px solid black';
            
            // Immediately update current hotspot color if one is selected
            if (currentHotspot) {
                currentHotspot.style.backgroundColor = getColorValue(selectedColor);
            }
        });
    });
    
    // Set default selected color
    const defaultColorOption = document.querySelector('.blue-color');
    if (defaultColorOption) {
        defaultColorOption.style.outline = '2px solid black';
    }
    
    previewImage.addEventListener('click', function() {
        if (this.src && this.src !== window.location.origin + '/') {
            createImageModal(this.src);
        }
    });

    /**
     * Track mouse movement for hotspot placement
     * @param {MouseEvent} event - Mouse event
     */
    function trackMouseMovement(event) {
        mouseX = event.clientX;
        mouseY = event.clientY;
        
        if (isPlacing) {
            hotspotPreview.style.left = (mouseX - 10) + 'px';
            hotspotPreview.style.top = (mouseY - 10) + 'px';
        }
    }
    
    /**
     * Start hotspot placement mode
     */
    function startHotspotPlacement() {
        isPlacing = true;
        hotspotPreview.style.display = 'block';
        hotspotPreview.style.backgroundColor = getColorValue(selectedColor);
        
        // Set initial position
        hotspotPreview.style.left = (mouseX - 10) + 'px';
        hotspotPreview.style.top = (mouseY - 10) + 'px';
        
        // Change cursor style
        document.body.style.cursor = 'crosshair';
    }
    
    /**
     * Cancel hotspot placement mode
     */
    function cancelHotspotPlacement() {
        isPlacing = false;
        hotspotPreview.style.display = 'none';
        document.body.style.cursor = 'auto';
    }
    
    /**
     * Handle model click for hotspot placement
     * @param {MouseEvent} event - Mouse event
     */
    function handleModelClick(event) {
        if (!isPlacing) return;
        
        // Get hit test result
        const hit = modelViewer.positionAndNormalFromPoint(event.clientX, event.clientY);
        if (hit) {
            const { position, normal } = hit;
            
            // Create new hotspot
            const hotspot = document.createElement('button');
            hotspot.slot = `hotspot-${hotspotCounter++}`;
            hotspot.id = `hotspot-id-${Math.random().toString(36).substring(2, 9)}`;
            hotspot.classList.add('hotspot');
            hotspot.style.width = '20px';
            hotspot.style.height = '20px';
            hotspot.style.borderRadius = '50%';
            hotspot.style.backgroundColor = getColorValue(selectedColor);
            hotspot.style.border = 'none';
            hotspot.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.25)';
            hotspot.style.cursor = 'pointer';
            
            // Store position and normal data
            hotspot.dataset.position = position.toString();
            hotspot.dataset.normal = normal.toString();
            // Initialize other required properties with empty values to avoid undefined
            hotspot.dataset.name = '';
            hotspot.dataset.text = '';
            hotspot.dataset.color = getColorValue(selectedColor);
            
            // Add to model viewer
            modelViewer.appendChild(hotspot);
            
            // Add click event listener
            hotspot.addEventListener('click', function(e) {
                e.stopPropagation();
                
                // Show edit panel
                const rect = this.getBoundingClientRect();
                console.log('Clicked hotspot position:', rect.left, rect.top, rect.right, rect.bottom);
                
                // Force the panel to be visible but position it correctly
                hotspotEditPanel.style.display = 'block';
                
                setTimeout(positionEditPanel.bind(null, this), 50);
                    
                // Fill form fields
                nameInput.value = this.dataset.name || '';
                textInput.value = this.dataset.text || '';
                
                // Update selected color based on hotspot's color
                const hotspotColor = this.style.backgroundColor;
                console.log('Hotspot color:', hotspotColor);
                
                // Match the color to our options
                let matchedColor = 'blue'; // default
                
                // Convert any color format to RGB values for consistent comparison
                const getRGBValues = (color) => {
                    // Create a temporary element to use the browser's color parsing
                    const tempElem = document.createElement('div');
                    tempElem.style.color = color;
                    document.body.appendChild(tempElem);
                    const computedColor = window.getComputedStyle(tempElem).color;
                    document.body.removeChild(tempElem);
                    
                    // Extract RGB values using regex
                    const rgbMatch = computedColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
                    if (rgbMatch) {
                        return [parseInt(rgbMatch[1]), parseInt(rgbMatch[2]), parseInt(rgbMatch[3])];
                    }
                    return null;
                };
                
                // Get RGB values for the hotspot color
                const hotspotRGB = getRGBValues(hotspotColor);
                console.log('Hotspot RGB values:', hotspotRGB);
                
                if (hotspotRGB) {
                    // Compare with known color values with some tolerance for variations
                    const closeToColor = (rgb, r, g, b, tolerance = 10) => {
                        return Math.abs(rgb[0] - r) <= tolerance && 
                               Math.abs(rgb[1] - g) <= tolerance && 
                               Math.abs(rgb[2] - b) <= tolerance;
                    };
                    
                    // Red: RGB approx (239, 68, 68)
                    if (closeToColor(hotspotRGB, 239, 68, 68)) {
    
                    matchedColor = 'red';
                    }
                    // Green: RGB approx (16, 185, 129)
                    else if (closeToColor(hotspotRGB, 16, 185, 129)) {
                        matchedColor = 'green';
                    }
                    // Blue: RGB approx (59, 130, 246)
                    else if (closeToColor(hotspotRGB, 59, 130, 246)) {
    
                    matchedColor = 'blue';
                    }
                    // Yellow: RGB approx (245, 158, 11)
                    else if (closeToColor(hotspotRGB, 245, 158, 11)) {
    
                    matchedColor = 'yellow';
                    }
                }
                
                console.log('Matched color:', matchedColor);
                
                // Update selected color
                selectedColor = matchedColor;
                
                // Update color option UI
                colorOptions.forEach(option => {
                    if (option.dataset.color === matchedColor) {
                        option.style.outline = '2px solid black';
                    } else {
                        option.style.outline = 'none';
                    }
                });
                
                // Show or hide image preview
                if (this.dataset.imageUrl && this.dataset.imageUrl !== '' && this.dataset.imageUrl !== 'null' && !this.dataset.imageUrl.endsWith('/')) {
                    previewImage.src = this.dataset.imageUrl;
                    
                    if (this.dataset.thumbnailUrl) {
                        imageHandler.setCurrentImageUrl(this.dataset.imageUrl, this.dataset.thumbnailUrl);
                    } else {
                        imageHandler.setCurrentImageUrl(this.dataset.imageUrl);
                    }
                    
                    document.getElementById('image-preview-container').classList.remove('hidden');
                } else {
                    imageHandler.resetImageUpload();
                }
                
                // Show delete button
                document.getElementById('delete-hotspot').style.display = 'inline-block';
                
                // Set current hotspot
                currentHotspot = this;
            });
            
            // Create annotation element
            const annotation = document.createElement('div');
            annotation.classList.add('annotation');
            annotation.dataset.hotspotId = hotspot.id;
            annotation.style.position = 'fixed';
            annotation.style.backgroundColor = 'white';
            annotation.style.opacity = '0.9';
            annotation.style.borderRadius = '6px';
            annotation.style.padding = '8px';
            annotation.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.25)';
            annotation.style.display = 'none';
            annotation.style.zIndex = '10000';
            document.body.appendChild(annotation);
            
            // Set initial annotation content
            let content = `
                <h3 style="margin: 0 0 4px 0; font-size: 14px; font-weight: bold;">${hotspot.dataset.name || ''}</h3>
                <p style="margin: 0; font-size: 12px;">${hotspot.dataset.text || ''}</p>
            `;
            annotation.innerHTML = content;
            
            // Show annotation on hover
            hotspot.addEventListener('mouseenter', () => {
                const rect = hotspot.getBoundingClientRect();
                annotation.style.display = 'block';
                
                // Position annotation properly
                positionAnnotation(annotation, rect);
            });
            
            // Hide annotation on mouse leave
            hotspot.addEventListener('mouseleave', () => {
                annotation.style.display = 'none';
            });
            
            // Exit placement mode
            cancelHotspotPlacement();
            
            // Automatically open edit panel for the new hotspot
            const rect = hotspot.getBoundingClientRect();
            console.log('Hotspot position:', rect.left, rect.top, rect.right, rect.bottom);
            
            // Force the panel to be visible but position it correctly after a short delay
            // This ensures the DOM has time to update
            hotspotEditPanel.style.display = 'block';

            setTimeout(positionEditPanel.bind(null, hotspot), 50);
            
            // Clear form fields for the new hotspot
            nameInput.value = '';
            textInput.value = '';
            
            // Reset image preview
            imageHandler.resetImageUpload();
            
            // Show delete button
            document.getElementById('delete-hotspot').style.display = 'inline-block';
            
            // Set current hotspot
            currentHotspot = hotspot;
           
           // Now that currentHotspot is set, ensure image properties are explicitly set to null
           if (currentHotspot) {
               // Using empty string instead of null directly in the dataset to avoid type issues
               currentHotspot.dataset.imageUrl = '';
               currentHotspot.dataset.thumbnailUrl = '';
           }
        }
    }
    
    /**
     * Save current hotspot data
     */
    function saveCurrentHotspot() {
        // Extra defensive check for valid currentHotspot
        if (!currentHotspot) {
            console.warn("Attempted to save hotspot, but currentHotspot is null");
            return;
        }
        
        // Update hotspot data - ensure we never set empty/null values
        // Add a defensive check to ensure currentHotspot is still valid
        try {
            // Handle each operation separately to make error tracing easier
            if (currentHotspot) {
                currentHotspot.dataset.name = nameInput.value || 'Untitled Hotspot';
                currentHotspot.dataset.text = textInput.value || '';
            }
        } catch (e) {
            console.error("Error updating currentHotspot:", e);
            cancelHotspotEdit(); // Close the panel and reset
            return;
        }
        
        try {
            // Update hotspot color
            currentHotspot.style.backgroundColor = getColorValue(selectedColor);
            currentHotspot.dataset.color = getColorValue(selectedColor);
        } catch (e) {
            console.error("Error updating hotspot color:", e);
        }
        
        try {
            // Handle image URL - ensure they're never undefined
            if (!imageHandler.isImageExplicitlyRemoved()) {
                const imageUrl = imageHandler.getCurrentImageUrl();
                const thumbnailUrl = imageHandler.getCurrentThumbnailUrl();
                
                if (imageUrl) {
                    currentHotspot.dataset.imageUrl = imageUrl;
                    
                    if (thumbnailUrl && thumbnailUrl !== imageUrl) {
                        currentHotspot.dataset.thumbnailUrl = thumbnailUrl;
                    }
                }
            } else {
                currentHotspot.dataset.imageUrl = '';
                currentHotspot.dataset.thumbnailUrl = '';
            }
        } catch (e) {
            console.error("Error handling image URLs:", e);
        }
        
        // Update annotation content
        const annotations = document.querySelectorAll('.annotation');
        annotations.forEach(annotation => {
            if (annotation.dataset.hotspotId === currentHotspot.id) {
                let content = `
                    <h3 style="margin: 0 0 4px 0; font-size: 14px; font-weight: bold;">${currentHotspot.dataset.name}</h3>
                    <p style="margin: 0; font-size: 12px;">${currentHotspot.dataset.text}</p>
                `;
                
                annotation.innerHTML = content;
                
                // Add image if available
                if (currentHotspot.dataset.imageUrl && currentHotspot.dataset.imageUrl !== '' && currentHotspot.dataset.imageUrl !== 'null' && !currentHotspot.dataset.imageUrl.endsWith('/')) {
                    try {
                        // Use thumbnail URL if available, otherwise use main image URL
                        const imageUrl = currentHotspot.dataset.thumbnailUrl || currentHotspot.dataset.imageUrl;
                        
                        // Ensure we use the worker URL for the image
                        let workerImageUrl = imageUrl;
                        if (!imageUrl.includes(ASSETS_WORKER_URL) && !imageUrl.startsWith('data:')) {
                            const pathMatch = imageUrl.match(/\/([^\/]+)$/);
                            workerImageUrl = pathMatch ? `${ASSETS_WORKER_URL}/${pathMatch[1]}` : imageUrl;
                        }
                        const img = createImage(workerImageUrl, currentHotspot.dataset.name, 'hotspot-thumbnail');
                        
                        annotation.appendChild(img);
                        
                        // Store the image URL directly on the img element for modal use
                        const fullImageUrl = currentHotspot.dataset.imageUrl;
                        img.addEventListener('click', function(e) {
                            e.preventDefault();
                            e.stopPropagation();
                            
                            // Ensure we use the worker URL for the modal image
                            let workerFullImageUrl = fullImageUrl;
                            if (!fullImageUrl.includes(ASSETS_WORKER_URL) && !fullImageUrl.startsWith('data:')) {
                                const pathMatch = fullImageUrl.match(/\/([^\/]+)$/);
                                workerFullImageUrl = pathMatch ? `${ASSETS_WORKER_URL}/${pathMatch[1]}` : fullImageUrl;
                            }
                            
                            createImageModal(fullImageUrl);
                            return false;
                        });
                    } catch (error) {
                        console.error('Error adding image to annotation:', error);
                    }
                }
            }
        });
        
        // Ensure annotation exists for this hotspot
        let annotationExists = false;
        const existingAnnotations = document.querySelectorAll('.annotation');
        existingAnnotations.forEach(annotation => {
            if (annotation.dataset.hotspotId === currentHotspot.id) {
                annotationExists = true;
            }
        });
        
        // Create new annotation if none exists
        if (!annotationExists) {
            // Store a reference to the current hotspot before it gets nullified
            // This prevents the error where we try to use currentHotspot after it's set to null
            const hotspotElement = currentHotspot;
            
            // Abort if for some reason the hotspot element is now invalid
            if (!hotspotElement) return;
            
            const newAnnotation = document.createElement('div');
            newAnnotation.classList.add('annotation');
            newAnnotation.dataset.hotspotId = currentHotspot.id;
            newAnnotation.style.position = 'fixed';
            newAnnotation.style.backgroundColor = 'white';
            newAnnotation.style.opacity = '0.9';
            newAnnotation.style.borderRadius = '6px';
            newAnnotation.style.padding = '8px';
            newAnnotation.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.25)';
            newAnnotation.style.display = 'none';
            newAnnotation.style.zIndex = '10000';
            document.body.appendChild(newAnnotation);
            
            // Set content
            let content = `
                <h3 style="margin: 0 0 4px 0; font-size: 14px; font-weight: bold;">${currentHotspot.dataset.name}</h3>
                <p style="margin: 0; font-size: 12px;">${currentHotspot.dataset.text}</p>
            `;
            newAnnotation.innerHTML = content;
            
            // Add image if available
            if (currentHotspot.dataset.imageUrl && currentHotspot.dataset.imageUrl !== '' && currentHotspot.dataset.imageUrl !== 'null' && !currentHotspot.dataset.imageUrl.endsWith('/')) {
                // Use thumbnail URL if available, otherwise use main image URL
                const imageUrl = currentHotspot.dataset.thumbnailUrl || currentHotspot.dataset.imageUrl;
                
                // Ensure we use the worker URL for the image
                let workerImageUrl = imageUrl;
                if (!imageUrl.includes(ASSETS_WORKER_URL) && !imageUrl.startsWith('data:')) {
                    const pathMatch = imageUrl.match(/\/([^\/]+)$/);
                    workerImageUrl = pathMatch ? `${ASSETS_WORKER_URL}/${pathMatch[1]}` : imageUrl;
                }
                
                const img = createImage(workerImageUrl, currentHotspot.dataset.name, 'hotspot-thumbnail');
                
                newAnnotation.appendChild(img);
               
                // Store the image URL directly on the img element for modal use
                const fullImageUrl = currentHotspot.dataset.imageUrl;
                
                img.addEventListener('click', function(e) {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    // Ensure we use the worker URL for the modal image
                    let workerFullImageUrl = fullImageUrl;
                    if (!fullImageUrl.includes(ASSETS_WORKER_URL) && !fullImageUrl.startsWith('data:')) {
                        const pathMatch = fullImageUrl.match(/\/([^\/]+)$/);
                        workerFullImageUrl = pathMatch ? `${ASSETS_WORKER_URL}/${pathMatch[1]}` : fullImageUrl;
                    }
                    createImageModal(workerFullImageUrl);
                    return false;
                });
            }
            
            // Set up hover event handlers for the hotspot
            // Add null check before attempting to add event listeners
            if (hotspotElement) {
                hotspotElement.addEventListener('mouseenter', () => {
                    const rect = hotspotElement.getBoundingClientRect();
                    newAnnotation.style.display = 'block';
                    
                    // Position annotation properly
                    positionAnnotation(newAnnotation, rect);
                });
                
                // Also add null check for mouseleave event
                hotspotElement.addEventListener('mouseleave', () => {
                    newAnnotation.style.display = 'none';
                });
 
            }
        }
        
        // Hide edit panel
        hotspotEditPanel.style.display = 'none';
        
        // Reset image explicitly removed flag
        imageHandler.setImageExplicitlyRemoved(false);
        
        // Check if model viewer is valid before saving
        if (!modelViewer || !modelViewer.src) {
            console.warn("Cannot save hotspots - model viewer is not ready");
            return;
        }
        
        // Save to server
        const modelId = extractModelIdFromUrl(modelViewer.src);
        saveHotspots(modelViewer.querySelectorAll('.hotspot'), modelId).catch(err => console.error('Failed to save hotspot changes:', err));
        
        // Clear current hotspot
        currentHotspot = null;
    }
    
    /**
     * Cancel hotspot editing
     */
    function cancelHotspotEdit() {
        hotspotEditPanel.style.display = 'none';
        currentHotspot = null;
        imageHandler.resetImageUpload();
    }
    
    /**
     * Delete current hotspot
     */
    async function deleteCurrentHotspot() {
        if (!currentHotspot) return;
        
        // Remove annotation
        const annotations = document.querySelectorAll('.annotation');
        annotations.forEach(annotation => {
            if (annotation.dataset.hotspotId === currentHotspot.id) {
                annotation.remove();
            }
        });
        
        // Remove hotspot
        currentHotspot.remove();
        
        // Hide edit panel
        hotspotEditPanel.style.display = 'none';
        
        // Save to server
        const modelId = extractModelIdFromUrl(modelViewer.src);
        await saveHotspots(modelViewer.querySelectorAll('.hotspot'), modelId).catch(err => console.error('Failed to save after deletion:', err));
        
        // Clear current hotspot
        currentHotspot = null;
    }
    
    /**
     * Get color value from color name
     * @param {string} colorName - Color name
     * @returns {string} - CSS color value
     */
    function getColorValue(colorName) {
        const colorMap = {
            'blue': '#3b82f6',
            'red': '#ef4444',
            'green': '#10b981',
            'yellow': '#f59e0b'
        };
        
        return colorMap[colorName] || colorMap['blue'];
    }
    
    /**
     * Position the edit panel based on hotspot position and screen size
     * @param {HTMLElement} hotspot - The hotspot element
     */
    function positionEditPanel(hotspot) {
        // Check device screen size
        const screenWidth = window.innerWidth;
        const isMobile = screenWidth <= 768;
        const isSmallMobile = screenWidth <= 480;
        
        // Get updated hotspot position
        const updatedRect = hotspot.getBoundingClientRect();
        console.log('Hotspot position for panel:', updatedRect.left, updatedRect.top, updatedRect.right, updatedRect.bottom);
        
        // Force panel to have absolute position
        hotspotEditPanel.style.position = 'absolute';
        
        // For mobile screens, always center the panel for better usability
        if (isMobile) {
            hotspotEditPanel.style.left = '50%';
            hotspotEditPanel.style.position = isSmallMobile ? 'fixed' : 'absolute';
            hotspotEditPanel.style.top = '50%';
            hotspotEditPanel.style.transform = 'translate(-50%, -50%)';
        } else {
            // Reset transform if coming from small screen
            hotspotEditPanel.style.transform = '';
            
            // Position the panel considering available space
            const rightSpace = window.innerWidth - updatedRect.right;
            const leftSpace = updatedRect.left;
            
            // Get panel dimensions
            hotspotEditPanel.style.maxWidth = '300px';
            const panelRect = hotspotEditPanel.getBoundingClientRect();
            
            // Decide horizontal position based on available space
            if (rightSpace >= panelRect.width + 20) {
                // Enough space on the right
                hotspotEditPanel.style.left = `${updatedRect.right + 20}px`;
            } else if (leftSpace >= panelRect.width + 20) {
                // Not enough space on right, but enough on left
                hotspotEditPanel.style.left = `${updatedRect.left - panelRect.width - 20}px`;
            } else {
                // Not enough space on either side, center horizontally
                hotspotEditPanel.style.left = `${Math.max(10, window.innerWidth/2 - panelRect.width/2)}px`;
            }
            
            // Vertical positioning
            if (updatedRect.top + panelRect.height + 10 <= window.innerHeight) {
                // Enough space below
                hotspotEditPanel.style.top = `${updatedRect.top}px`;
            } else {
                // Not enough space below, position at bottom with margin
                const topPosition = Math.max(10, window.innerHeight - panelRect.height - 20);
                hotspotEditPanel.style.top = `${topPosition}px`;
            }
        }
        
        console.log('Final panel position:', hotspotEditPanel.style.left, hotspotEditPanel.style.top);
    }
    
    /**
     * Position annotation element relative to a hotspot
     * @param {HTMLElement} annotation - The annotation element
     * @param {DOMRect} hotspotRect - The hotspot's bounding client rect
     */
    function positionAnnotation(annotation, hotspotRect) {
        const isMobile = window.innerWidth <= 768;
        const isSmallMobile = window.innerWidth <= 480;
        
        // First set initial position to measure size
        annotation.style.left = `${hotspotRect.right + 10}px`;
        annotation.style.top = `${hotspotRect.top}px`;
        
        // Get annotation dimensions
        const annotRect = annotation.getBoundingClientRect();
        
        // Horizontal positioning
        const rightSpace = window.innerWidth - hotspotRect.right;
        
        if (rightSpace >= annotRect.width + 10) {
            // Enough space on the right
            annotation.style.left = `${hotspotRect.right + 10}px`;
        } else {
            // Not enough space on right, try left
            annotation.style.left = `${Math.max(5, hotspotRect.left - annotRect.width - 10)}px`;
            
            // If too large for left placement on mobile, center it
            if (isSmallMobile && hotspotRect.left < annotRect.width + 10) {
                annotation.style.left = '50%';
                annotation.style.transform = 'translateX(-50%)';
                annotation.style.maxWidth = '90%';
            }
        }
        
        // Vertical positioning - handle small screens
        if (hotspotRect.top + annotRect.height > window.innerHeight) {
            // If annotation would go off bottom, place it above if possible
            if (hotspotRect.top - annotRect.height > 0) {
                annotation.style.top = `${hotspotRect.top - annotRect.height}px`;
            } else {
                // Otherwise place at bottom with margin
                annotation.style.top = `${window.innerHeight - annotRect.height - 10}px`;
            }
        }
    }
    
    return {
        trackMouseMovement,
        startHotspotPlacement,
        cancelHotspotPlacement,
        handleModelClick,
        saveCurrentHotspot,
        cancelHotspotEdit,
        deleteCurrentHotspot,
        isPlacingHotspot: () => isPlacing,
        getCurrentHotspot: () => currentHotspot,
        setCurrentHotspot: (hotspot) => { currentHotspot = hotspot; },
        setHotspotCounter: (count) => { hotspotCounter = count; }
    };
}

/**
 * Create a standard image element
 * @param {string} src - Image source URL
 * @param {string} alt - Alt text
 * @param {string} className - CSS class
 * @returns {HTMLImageElement} - The created image element
 */
function createImage(src, alt, className) {
    // Create a new image
    
    // Convert problematic URLs to direct bucket URLs
    // Ensure we're using the worker URL for all asset URLs
    let workerSrc = src;
    if (src && !src.includes(ASSETS_WORKER_URL) && !src.startsWith('data:')) {
        // Extract the filename/path from the URL and use with worker
        const pathMatch = src.match(/\/([^\/]+)$/);
        workerSrc = pathMatch ? `${ASSETS_WORKER_URL}/${pathMatch[1]}` : src;
    }
    
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.alt = alt || '';
    img.className = className || '';
    
    // Only set src if it's valid
    if (workerSrc && workerSrc !== '' && workerSrc !== 'null' && !workerSrc.endsWith('/')) {
        // Prevent using base64 images
        if (src.startsWith('data:image')) {
            console.warn('Detected base64 image in createImage, image will not be displayed');
            // Log the first 100 chars of the base64 data for debugging
            console.log('Base64 data starts with:', src.substring(0, 100) + '...');
            // Don't set src - we'll avoid using base64 data
        } else {
            // Add error handling to display visual feedback when images fail to load
           img.onerror = function() {
               console.error('Failed to load image in createImage:', workerSrc);
               img.classList.add('error');
               // Ensure proper CSS units are used
               img.style.width = '100px';
 
               img.style.height = '60px';
               img.style.backgroundColor = '#f0f0f0'; // Add background to show error state
               img.alt = 'Image failed to load - please try again';
           };
           
           // URL encode any spaces in the image URL
           img.src = workerSrc.replace(/ /g, '%20');
           
           // Add crossorigin attribute to handle CORS issues in production
           // This helps with images loaded from different domains
           img.crossOrigin = 'anonymous';
           
           // Add a retry mechanism for image loading
           setTimeout(() => {
               if (img.naturalWidth === 0) img.src = workerSrc.replace(/ /g, '%20') + '?retry=' + Date.now();
           }, 2000);
        }
    }
    
    return img;
}